/**
 * Начало и конец сессии кабинета в браузере поверх хранилища аккаунтов (KAN-925).
 * Вход добавляет аккаунт к вошедшим (тот же пользователь обновляется), выход
 * убирает только текущий аккаунт вкладки, "выйти из всех" убирает все.
 * KAN-753: менеджер сессии хранится по пользователю; вход и выход стирают
 * только выбор этого пользователя, поэтому окно "Кто работает с клиентами?"
 * показывается после каждого его входа, а другие аккаунты свой выбор помнят.
 */
import { clearApiFileCache } from './apiFileCache';
import {
  LEGACY_ACCOUNT_ID,
  findAccount,
  getActiveAccount,
  readAccounts,
  removeAccount,
  removeAllAccounts,
  saveAccountFromLogin,
  updateAccountTokens,
  userIdFromToken,
  type Account,
  type AccountUserLike,
} from './accounts';
import { clearSessionManager } from './sessionManager';
import { revokeRefreshToken } from '@/services/api/authLogout';

/**
 * Вход: аккаунт сохраняется и становится активным во вкладке. Пользователь из
 * ответа входа; без него userId берётся из JWT (иначе временный ключ до /auth/me).
 */
export function startAuthSession(
  access: string,
  refresh?: string | null,
  user?: AccountUserLike | null
): Account {
  const who: AccountUserLike = user?.id
    ? user
    : { id: userIdFromToken(access) ?? LEGACY_ACCOUNT_ID };
  clearSessionManager(who.id);
  return saveAccountFromLogin(access, refresh, who);
}

/**
 * KAN-834: новая пара токенов после обновления (auth/refresh/) для аккаунта,
 * отправившего запрос (по умолчанию активного). Менеджер сессии и кеш файлов
 * не трогаются. refresh приходит при ротации, старый уже в чёрном списке.
 */
export function updateAuthTokens(access: string, refresh?: string | null, userId?: string): void {
  const id = userId ?? getActiveAccount()?.userId;
  if (id) updateAccountTokens(id, access, refresh);
}

export function getRefreshToken(userId?: string): string | null {
  const account = userId ? findAccount(userId) : getActiveAccount();
  return account?.refresh ?? null;
}

/**
 * Выход из текущего аккаунта вкладки; возвращает следующий активный (или null).
 * KAN-925: refresh аккаунта отзывается на backend (best effort).
 */
export function endAuthSession(): Account | null {
  const active = getActiveAccount();
  if (active) revokeRefreshToken(active.access, active.refresh);
  const next = active ? removeAccount(active.userId) : null;
  // KAN-770: картинки и файлы прошлого пользователя не переживают выход.
  clearApiFileCache();
  return next;
}

/** Выход из всех аккаунтов браузера. */
export function endAllAuthSessions(): void {
  readAccounts().forEach((a) => revokeRefreshToken(a.access, a.refresh));
  removeAllAccounts();
  clearApiFileCache();
}
